/**
 * TestDriveHistoryCard — Jun-2026 (User-Wunsch).
 *
 * Probefahrt-Historie im Händler-Cockpit. Listet die durchgeführten
 * Probefahrten (`GET /api/dealer/test-drives`), ermöglicht das digitale
 * Erfassen des Rückgabe-km-Stands (+ Datum, Schäden) und schließt die
 * Probefahrt mit automatischer Mehrkilometer-Abrechnung ab. Zusätzlich:
 * PDF erneut laden und Vereinbarung per E-Mail an den Kunden senden.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Car, Loader2, RefreshCcw, Download, Mail, CheckCircle2, Gauge, Printer, ShoppingCart, UserX, Archive, HelpCircle, Flag, PenLine, QrCode } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { previewPdfResponse, customerRecipient } from "../../lib/pdfPreview";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { OnSiteSignatureModal } from "../listing/dispo/OnSiteSignatureModal";

const eur = (n) => (Number(n) || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; } };

// Jun-2026 (User-Wunsch) — Probefahrt-Ergebnis: 4 Ausgänge.
const OUTCOME_BADGES = {
    bought: { label: "Gekauft", cls: "bg-emerald-100 text-emerald-800" },
    no_show: { label: "Nicht erschienen · Lead", cls: "bg-amber-100 text-amber-800" },
    declined: { label: "Archiviert", cls: "bg-gray-100 text-gray-600" },
    undecided: { label: "Unentschieden · Lead", cls: "bg-blue-100 text-blue-800" },
};

function OutcomeButtons({ td, onPicked }) {
    const [busy, setBusy] = useState(null);
    const navigate = useNavigate();

    const pick = async (outcome) => {
        setBusy(outcome);
        try {
            const r = await api.post(`/test-drives/${td.id}/outcome`, { outcome });
            if (outcome === "bought") {
                toast.success("Als gekauft markiert — weiter im Verkaufsprozess.");
                const lid = r.data?.listing_id || td.listing_id;
                navigate(`/edit-listing/${lid}?dispo=sell`);
                return;
            }
            if (outcome === "no_show" || outcome === "undecided") toast.success('Als Lead in „Meine Leads" übernommen.');
            else toast.success("Vorgang archiviert.");
            onPicked?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen."); }
        finally { setBusy(null); }
    };

    const btn = "inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border disabled:opacity-60";
    return (
        <div className="mt-2 bg-[#FFFBEB]/60 border border-[#FFCC00]/60 rounded-sm p-3" data-testid={`td-outcome-${td.id}`}>
            <div className="flex items-center gap-1.5 mb-2 text-xs font-bold text-[#111827]">
                <Flag className="w-3.5 h-3.5 text-[#FFCC00]" /> Probefahrt-Ergebnis erfassen
            </div>
            <div className="flex flex-wrap gap-1.5">
                <button type="button" disabled={busy} onClick={() => pick("bought")} className={`${btn} border-emerald-600 text-emerald-700 hover:bg-emerald-50`} data-testid={`td-outcome-bought-${td.id}`}>
                    {busy === "bought" ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShoppingCart className="w-3 h-3" />} Hat gekauft
                </button>
                <button type="button" disabled={busy} onClick={() => pick("no_show")} className={`${btn} border-amber-500 text-amber-700 hover:bg-amber-50`} data-testid={`td-outcome-no-show-${td.id}`}>
                    {busy === "no_show" ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserX className="w-3 h-3" />} Nicht erschienen
                </button>
                <button type="button" disabled={busy} onClick={() => pick("undecided")} className={`${btn} border-blue-600 text-blue-700 hover:bg-blue-50`} data-testid={`td-outcome-undecided-${td.id}`}>
                    {busy === "undecided" ? <Loader2 className="w-3 h-3 animate-spin" /> : <HelpCircle className="w-3 h-3" />} Unentschieden
                </button>
                <button type="button" disabled={busy} onClick={() => pick("declined")} className={`${btn} border-gray-400 text-gray-700 hover:bg-gray-100`} data-testid={`td-outcome-declined-${td.id}`}>
                    {busy === "declined" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Archive className="w-3 h-3" />} Will nicht kaufen
                </button>
            </div>
            <p className="text-[10px] text-gray-500 mt-1.5">„Hat gekauft" startet den Verkaufsprozess · „Nicht erschienen"/„Unentschieden" legt einen Lead in „Meine Leads" an · „Will nicht kaufen" archiviert.</p>
        </div>
    );
}

function ReturnForm({ td, onDone }) {
    const [km, setKm] = useState("");
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [damages, setDamages] = useState("");
    const [busy, setBusy] = useState(false);
    const startKm = Number(td.vehicle?.start_km) || 0;
    const freeKm = Number(td.costs?.free_km) || 0;
    const price = Number(td.costs?.extra_km_price) || 0;
    const driven = km !== "" ? Math.max(0, Number(km) - startKm) : null;
    const extra = driven != null ? Math.max(0, driven - freeKm) : null;
    const extraCost = extra != null ? extra * price : null;

    const save = async () => {
        if (km === "" || Number(km) < startKm) { toast.error(`Rückgabe-km muss ≥ Anfangs-km (${startKm}) sein.`); return; }
        setBusy(true);
        try {
            await api.patch(`/test-drives/${td.id}`, { return_km: Number(km), return_date: date, return_damages: damages });
            toast.success("Probefahrt abgeschlossen.");
            onDone?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen."); }
        finally { setBusy(false); }
    };

    return (
        <div className="mt-2 bg-gray-50 border border-gray-200 rounded-sm p-3" data-testid={`td-return-form-${td.id}`}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Rückgabe-km</Label>
                    <Input type="number" value={km} onChange={(e) => setKm(e.target.value)} placeholder={`≥ ${startKm}`} className="rounded-sm font-mono-num mt-1" data-testid={`td-return-km-${td.id}`} />
                </div>
                <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Rückgabe-Datum</Label>
                    <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-sm mt-1" data-testid={`td-return-date-${td.id}`} />
                </div>
                <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Schäden bei Rückgabe</Label>
                    <Input value={damages} onChange={(e) => setDamages(e.target.value)} placeholder="keine" className="rounded-sm mt-1" data-testid={`td-return-damages-${td.id}`} />
                </div>
            </div>
            {driven != null && (
                <div className="text-xs mt-2 flex flex-wrap gap-x-4 gap-y-1" data-testid={`td-return-calc-${td.id}`}>
                    <span>Gefahren: <strong className="font-mono-num">{driven} km</strong></span>
                    <span>Frei: <strong className="font-mono-num">{freeKm} km</strong></span>
                    <span>Mehrkilometer: <strong className="font-mono-num">{extra} km</strong></span>
                    <span className="text-[#111827]">Berechnung: <strong className="font-mono-num">{eur(extraCost)}</strong></span>
                </div>
            )}
            <div className="flex justify-end mt-2">
                <Button onClick={save} disabled={busy} className="rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs h-8" data-testid={`td-return-save-${td.id}`}>
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
                    Probefahrt abschließen
                </Button>
            </div>
        </div>
    );
}

export default function TestDriveHistoryCard() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openId, setOpenId] = useState(null);
    const [busy, setBusy] = useState(null);
    const [signTd, setSignTd] = useState(null); // Vor-Ort-Signatur (Mitarbeiter-Gerät)
    const [signBusy, setSignBusy] = useState(false);
    const [portal, setPortal] = useState(null); // { tdId, portal_url, qr_url }

    const load = async () => {
        setLoading(true);
        try { const r = await api.get("/dealer/test-drives"); setItems(r.data.items || []); }
        catch { /* silent */ }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const downloadPdf = async (td) => {
        setBusy(`pdf-${td.id}`);
        try {
            const resp = await api.get(`/test-drives/${td.id}/pdf`, { responseType: "blob" });
            previewPdfResponse(resp, `Probefahrt-${td.customer?.last_name || "Benutzer"}.pdf`, { recipient: customerRecipient(td.customer) });
        } catch { toast.error("PDF konnte nicht geladen werden."); }
        finally { setBusy(null); }
    };

    const printPdf = async (td) => {
        setBusy(`print-${td.id}`);
        try {
            const resp = await api.get(`/test-drives/${td.id}/pdf`, { responseType: "blob" });
            previewPdfResponse(resp, `Probefahrt-${td.customer?.last_name || "Benutzer"}.pdf`, { recipient: customerRecipient(td.customer) });
        } catch { toast.error("PDF konnte nicht geladen werden."); }
        finally { setBusy(null); }
    };

    const sendEmail = async (td) => {
        setBusy(`mail-${td.id}`);
        try {
            await api.post(`/test-drives/${td.id}/send-email`);
            toast.success(`Vereinbarung an ${td.customer?.email} gesendet.`);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Versand fehlgeschlagen."); }
        finally { setBusy(null); }
    };

    // Jul-2026 (User-Wunsch) — digitale Unterschrift am Mitarbeiter-Gerät.
    const submitOnsiteSig = async (sigs, place) => {
        if (!signTd) return;
        setSignBusy(true);
        try {
            await api.post(`/test-drives/${signTd.id}/sign-onsite`, {
                customer_signature: sigs.customer || "", dealer_signature: sigs.dealer || "", place,
            });
            toast.success("Unterschrift in die Probefahrt-Vereinbarung eingebettet");
            setSignTd(null);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Unterschrift konnte nicht gespeichert werden"); }
        finally { setSignBusy(false); }
    };

    // Jul-2026 (User-Wunsch) — Signatur-Link/QR für das Kunden-Handy.
    const openPortal = async (td) => {
        setBusy(`portal-${td.id}`);
        try {
            const r = await api.post(`/test-drives/${td.id}/sign-portal`);
            setPortal({ tdId: td.id, ...r.data });
            toast.success("Signatur-Link erstellt — QR am Kundengerät scannen");
        } catch (e) { toast.error(e.response?.data?.detail || "Link konnte nicht erstellt werden"); }
        finally { setBusy(null); }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-5 mt-4" data-testid="test-drive-history-card">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Car className="w-5 h-5 text-[#111827]" />
                    <h3 className="font-display font-bold text-base">Durchgeführte Probefahrten</h3>
                </div>
                <button onClick={load} className="text-xs text-gray-500 hover:text-gray-900 flex items-center gap-1" data-testid="td-history-refresh">
                    <RefreshCcw className="w-3 h-3" /> Aktualisieren
                </button>
            </div>

            {loading ? (
                <div className="flex items-center gap-2 justify-center py-6 text-gray-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Lade Probefahrten…</div>
            ) : items.length === 0 ? (
                <div className="text-sm text-gray-500 py-6 text-center" data-testid="td-history-empty">
                    Noch keine Probefahrten durchgeführt. Starte eine über „Probefahrt durchführen“ in der Disposition.
                </div>
            ) : (
                <ul className="divide-y divide-gray-100 max-h-[40rem] overflow-auto" data-testid="td-history-list">
                    {items.map((td) => {
                        const closed = td.status === "closed";
                        const outcome = td.outcome || null;
                        const outcomeBadge = outcome ? OUTCOME_BADGES[outcome] : null;
                        return (
                            <li key={td.id} className="py-3" data-testid={`td-history-${td.id}`}>
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-bold truncate">{td.listing_title}</div>
                                        <div className="text-xs text-gray-600 mt-0.5">
                                            <strong>{td.customer?.first_name} {td.customer?.last_name}</strong>
                                            <span className="ml-2">{td.vehicle?.manufacturer} {td.vehicle?.model}</span>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                                            <span>Von: {td.period?.from || "—"}</span>
                                            <span>Bis: {td.period?.to || "—"}</span>
                                            <span className="inline-flex items-center gap-1"><Gauge className="w-3 h-3" /> Start: <strong className="font-mono-num">{td.vehicle?.start_km ?? "—"} km</strong></span>
                                            {closed && <span className="inline-flex items-center gap-1">Rückgabe: <strong className="font-mono-num">{td.return_km} km</strong></span>}
                                            {closed && <span className="text-[#111827]">Mehr-km: <strong className="font-mono-num">{eur(td.extra_km_cost)}</strong></span>}
                                        </div>
                                        <div className="text-[10px] text-gray-400 mt-1">Erstellt: {fmt(td.created_at)}{td.email_sent ? " · per E-Mail gesendet" : ""}</div>
                                    </div>
                                    <div className="flex flex-col items-end gap-1 shrink-0">
                                        <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm ${closed ? "bg-gray-100 text-gray-700" : "bg-amber-100 text-amber-800"}`}>
                                            {closed ? "Abgeschlossen" : "Aktiv"}
                                        </span>
                                        {outcomeBadge && (
                                            <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm ${outcomeBadge.cls}`} data-testid={`td-outcome-badge-${td.id}`}>
                                                {outcomeBadge.label}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    {!closed && (
                                        <button type="button" onClick={() => setOpenId(openId === td.id ? null : td.id)}
                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                                            data-testid={`td-return-toggle-${td.id}`}>
                                            <Gauge className="w-3 h-3" /> Rückgabe erfassen
                                        </button>
                                    )}
                                    <button type="button" onClick={() => downloadPdf(td)} disabled={busy === `pdf-${td.id}`}
                                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-60"
                                        data-testid={`td-pdf-${td.id}`}>
                                        <Download className="w-3 h-3" /> PDF
                                    </button>
                                    <button type="button" onClick={() => printPdf(td)} disabled={busy === `print-${td.id}`}
                                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-60"
                                        data-testid={`td-print-${td.id}`}>
                                        <Printer className="w-3 h-3" /> Drucken
                                    </button>
                                    {td.customer?.email && (
                                        <button type="button" onClick={() => sendEmail(td)} disabled={busy === `mail-${td.id}`}
                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border border-blue-600 text-blue-700 hover:bg-blue-50 disabled:opacity-60"
                                            data-testid={`td-email-${td.id}`}>
                                            <Mail className="w-3 h-3" /> Per E-Mail
                                        </button>
                                    )}
                                    <button type="button" onClick={() => setSignTd(td)}
                                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border border-orange-500 text-orange-700 hover:bg-orange-50"
                                        data-testid={`td-sign-onsite-${td.id}`}>
                                        <PenLine className="w-3 h-3" /> Signieren (dieses Gerät)
                                    </button>
                                    <button type="button" onClick={() => openPortal(td)} disabled={busy === `portal-${td.id}`}
                                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border border-indigo-500 text-indigo-700 hover:bg-indigo-50 disabled:opacity-60"
                                        data-testid={`td-sign-portal-${td.id}`}>
                                        <QrCode className="w-3 h-3" /> Kunden-Handy (QR)
                                    </button>
                                    {(td.onsite_signatures?.customer || td.onsite_signatures_return?.customer) && (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`td-signed-badge-${td.id}`}>
                                            <CheckCircle2 className="w-3 h-3" /> Digital signiert{td.onsite_signatures_return?.customer ? " (inkl. Rückgabe)" : ""}
                                        </span>
                                    )}
                                </div>
                                {portal?.tdId === td.id && (
                                    <div className="mt-2 bg-indigo-50 border border-indigo-200 rounded-sm p-3 flex items-start gap-3" data-testid={`td-portal-box-${td.id}`}>
                                        <img src={portal.qr_url} alt="QR" className="w-24 h-24 bg-white border border-indigo-200 rounded-sm p-1" data-testid={`td-portal-qr-${td.id}`} />
                                        <div className="flex-1 min-w-0 space-y-1.5">
                                            <div className="text-xs font-bold text-indigo-900">QR mit dem Kunden-Handy scannen — der Kunde verifiziert sich mit dem Nachnamen und unterschreibt auf dem Touchscreen.</div>
                                            <div className="flex items-center gap-2">
                                                <input readOnly value={portal.portal_url} className="flex-1 text-[11px] font-mono bg-white border border-indigo-200 rounded-sm px-2 py-1 truncate" data-testid={`td-portal-url-${td.id}`} />
                                                <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={() => { navigator.clipboard?.writeText(portal.portal_url); toast.success("Link kopiert"); }} data-testid={`td-portal-copy-${td.id}`}>Kopieren</Button>
                                            </div>
                                            <button type="button" onClick={() => setPortal(null)} className="text-[11px] text-indigo-700 underline" data-testid={`td-portal-close-${td.id}`}>Schließen</button>
                                        </div>
                                    </div>
                                )}
                                {openId === td.id && !closed && (
                                    <ReturnForm td={td} onDone={() => { setOpenId(null); load(); }} />
                                )}
                                {!outcome && <OutcomeButtons td={td} onPicked={load} />}
                            </li>
                        );
                    })}
                </ul>
            )}
            {signTd && (
                <OnSiteSignatureModal
                    title={`Probefahrt-Vereinbarung — ${signTd.status === "closed" ? "Rückgabe" : "Übergabe"} signieren`}
                    fields={[{ key: "customer", label: "Unterschrift Benutzer/Kunde" }, { key: "dealer", label: "Unterschrift Überlasser (optional)" }]}
                    busy={signBusy}
                    onClose={() => setSignTd(null)}
                    onSubmit={submitOnsiteSig}
                />
            )}
        </div>
    );
}
